import type { PropsWithChildren } from 'react';
import * as Headless from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

import { AlertStatus } from '@/entities/alert-status';
import { cls } from '@/utils/cls';

import { StatusIcon } from './AlertStatusBadge';

export type DisclosureProps = Omit<Headless.DisclosureProps<'div'>, 'as'> & {
  className?: string;
};

export function Disclosure({ children, className, ...rest }: DisclosureProps) {
  return (
    <div className={className}>
      <Headless.Disclosure defaultOpen={true} {...rest}>
        {children}
      </Headless.Disclosure>
    </div>
  );
}

export type DisclosureTitleProps = {
  status?: AlertStatus; // Active status from BE
  inactive?: boolean; // Force neutral grey dot when true
  onClick?: () => void;
  className?: string;
};

export function DisclosureTitle({
  children,
  status,
  inactive = false,
  onClick,
  className,
}: PropsWithChildren<DisclosureTitleProps>) {
  // Decide whether we should render any dot
  const showDot = status !== undefined || inactive;

  return (
    <Headless.Disclosure.Button
      onClick={onClick}
      className={cls(
        className,
        'flex w-full cursor-pointer items-center justify-between border-b border-gray-900/10 px-4 py-3 focus:outline-none focus-visible:ring focus-visible:ring-purple-500 focus-visible:ring-opacity-75 ui-open:border-0',
      )}
    >
      <div className="flex items-center space-x-3">
        {showDot && <StatusIcon status={status} />}
        <div className="text-lg font-medium text-gray-900">{children}</div>
      </div>
      <div className="flex items-center justify-center rounded-full p-1 hover:bg-gray-900/5">
        <ChevronDownIcon
          className={cls(
            'h-6 w-6 text-gray-900/80 transition-transform duration-300 ui-open:rotate-180',
          )}
        />
      </div>
    </Headless.Disclosure.Button>
  );
}

export type DisclosurePanelProps = { className?: string };

export function DisclosurePanel({
  children,
  className,
}: PropsWithChildren<DisclosurePanelProps>) {
  return (
    <Headless.Disclosure.Panel className={className}>
      {children}
    </Headless.Disclosure.Panel>
  );
}
